/**
 * RBAC v2 — feature access map for route and navigation gating.
 *
 * A page is accessible if the user holds EITHER the feature's `:view` or its
 * `:manage` permission (managing implies being able to see). Pages that span
 * multiple features are accessible with any of the listed features.
 *
 * This is the single source of truth shared by:
 * - The route guard (`WithUser` in RouteGuards.js) — renders <AccessDenied/>.
 * - The sidebar (`AppSidebar.js`) — hides links the user cannot open.
 *
 * NOTE: This is a UX layer only. The authoritative security boundary is the
 * backend `require_permission` / `require_feature_read` checks on the API.
 */

/** Return the pair of permissions ([`feature:view`, `feature:manage`]) that grant read access. */
export const readAccess = (feature) => [`${feature}:view`, `${feature}:manage`];

/**
 * Permissions that grant access to each gated app route. A user needs ANY of
 * the listed permissions. Routes not present here are open to all authenticated
 * users (e.g. settings self-gates its sections; my-integrations/refer are ungated).
 */
export const ROUTE_ACCESS = {
  '/app/dashboard': readAccess('accounts'),
  '/app/accounts': readAccess('accounts'),
  '/app/onboarding': readAccess('onboarding'),
  // AI Actions surfaces account priorities, campaigns, workflows, and approvals.
  '/app/ai-actions': [
    ...readAccess('accounts'),
    ...readAccess('campaigns'),
    ...readAccess('workflows'),
  ],
  '/app/communications': readAccess('communications'),
  '/app/analytics': readAccess('analytics'),
  '/app/meetings': readAccess('meetings'),
  '/app/tasks-board': readAccess('tasks'),
  '/app/knowledge-base': readAccess('knowledge_base'),
  '/app/chat': readAccess('assistant'),
  '/app/opportunities': readAccess('accounts'),
};
